import { useParamOrNull } from 'dg-web-shared/lib/ReactRouterHelpers.ts';
import { Navigate, useNavigate } from 'react-router';
import {
    RequestMethod,
    useNavigateOnSuccess,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useState } from 'react';
import {
    AuthedValidationContent,
    AuthValidationErrorReason,
    AuthValidationSuccess,
    AuthValidationSuccessResponse,
    ConfirmEmailPopup,
    LinkLoginData,
    LinkLoginPopup,
    OnboardingCompletePopup,
    OnboardingPendingPopup,
    PublicValidationErrorPopup,
    PublicValidationErrorReason,
    PublicValidationResponse,
    PublicValidationState,
    useResentVerificationMail,
    ValidationOrigin,
    ValidationState,
} from './ParkingaboSharedRegistrationValidationRoute.tsx';
import { ParkingaboAsyncLoadedSection } from '../../components/layout/ParkingaboAsyncLoadedSection.tsx';
import { LoggedInRouteCompProps } from '../RouteUtils.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';

export function usePublicBadgeValidationState(badgeLabelNr: string | null) {
    const [badgeValidationState] = useServerFetch<
        PublicValidationResponse,
        { badgeLabelNr: string },
        { reason: PublicValidationErrorReason }
    >(
        ({ badgeLabelNr }) => ({
            url: `/ui-api/parkingabo/badge/${badgeLabelNr}/validation`,
        }),
        badgeLabelNr ? { badgeLabelNr: badgeLabelNr } : null,
    );

    return [badgeValidationState];
}

export function PublicParkingaboBadgeValidationRoute() {
    const badgeLabelNr = useParamOrNull('badgeLabelNr');
    const navigate = useNavigate();

    const [badgeValidationState] = usePublicBadgeValidationState(badgeLabelNr);

    const { anotherLinkState, requestAnotherLink } =
        useResentVerificationMail();
    useNavigateOnSuccess(
        anotherLinkState,
        `/register-with-badge/${badgeLabelNr}/email-sent`,
    );

    if (badgeLabelNr === null) {
        return <Navigate to="/login" replace />;
    }

    return (
        <ParkingaboAsyncLoadedSection
            state={badgeValidationState}
            render={data => {
                switch (data.validationState) {
                    case PublicValidationState.NO_LOGIN:
                        return (
                            <Navigate
                                to={`/register-with-badge/${badgeLabelNr}`}
                            />
                        );
                    case PublicValidationState.EMAIL_CONFIRMATION_PENDING:
                        return (
                            <ConfirmEmailPopup
                                emailToBeConfirmed={data.email!}
                                onAbort={() =>
                                    requestAnotherLink({
                                        email: data.email!,
                                    })
                                }
                                onConfirm={() =>
                                    navigate(
                                        `/register-with-badge/${badgeLabelNr}`,
                                    )
                                }
                            />
                        );
                    case PublicValidationState.ONBOARDING_PENDING:
                        return (
                            <OnboardingPendingPopup
                                onConfirm={() => navigate('/login')}
                                origin={ValidationOrigin.BADGE}
                            />
                        );
                    case PublicValidationState.ONBOARDING_COMPLETED:
                        return (
                            <OnboardingCompletePopup
                                onConfirm={() => navigate('/login')}
                                origin={ValidationOrigin.BADGE}
                            />
                        );
                }
            }}
            renderError={errorData => (
                <PublicValidationErrorPopup
                    error={errorData}
                    origin={ValidationOrigin.BADGE}
                    badgeLabelNr={badgeLabelNr}
                />
            )}
        />
    );
}

export function AuthedParkingaboBadgeValidationRoute({
    logout,
    linkedUsers,
    refetchUsers,
}: LoggedInRouteCompProps) {
    const badgeLabelNr = useParamOrNull('badgeLabelNr');
    const navigate = useNavigate();
    const [linkLoginData, setLinkLoginData] = useState<LinkLoginData | null>(
        null,
    );
    const [validationState, setValidationState] = useState<ValidationState>(
        ValidationState.LOADING,
    );

    const [badgeValidationState] = useServerFetch<
        AuthValidationSuccess,
        { badgeLabelNr: string },
        { reason: AuthValidationErrorReason }
    >(
        ({ badgeLabelNr }) => ({
            url: `/ui-api/parkingabo/user/badge/${badgeLabelNr}/validation`,
        }),
        badgeLabelNr ? { badgeLabelNr: badgeLabelNr } : null,
    );

    const [linkCustomerToLoginState, linkCustomerToLogin] = useServerWrite<
        {
            badgeLabelNr: string;
        },
        object,
        ValidationData
    >(() => ({
        url: `/ui-api/parkingabo/user/badge/${badgeLabelNr}/link-login`,
        method: RequestMethod.PUT,
    }));

    useServerSuccessEffect(badgeValidationState, data => {
        switch (data.response) {
            case AuthValidationSuccessResponse.LINK_LOGIN:
                setLinkLoginData({
                    email: data.email,
                    operatorName: data.operatorName,
                });
                break;
            case AuthValidationSuccessResponse.LOGGED_IN:
                navigate(`/${data.operatorExternalId}`);
                break;
        }
    });

    useServerErrorEffect(badgeValidationState, (_, data) => {
        if (data === null) {
            setValidationState(ValidationState.GENERIC_ERROR);
        } else {
            switch (data.reason) {
                case AuthValidationErrorReason.NOT_FOUND:
                    setValidationState(ValidationState.NOT_FOUND);
                    break;
                case AuthValidationErrorReason.ALREADY_REGISTERED_ON_OPERATOR:
                    setValidationState(
                        ValidationState.ALREADY_REGISTERED_ON_OPERATOR,
                    );
                    break;
                case AuthValidationErrorReason.INDIVIDUAL_PAYMENT_NO_APP_TENANT:
                    setValidationState(
                        ValidationState.INDIVIDUAL_PAYMENT_NO_APP_TENANT,
                    );
                    break;
            }
        }
    });

    if (badgeLabelNr === null) {
        return <Navigate to="/" replace />;
    }

    if (linkLoginData !== null) {
        return (
            <LinkLoginPopup
                linkLoginData={linkLoginData}
                onConfirm={() =>
                    linkCustomerToLogin({ badgeLabelNr: badgeLabelNr })
                }
                linkCustomerToLoginState={linkCustomerToLoginState}
                onAbort={() => logout()}
                linkedUsers={linkedUsers}
                refetchUsers={refetchUsers}
            />
        );
    }

    return (
        <AuthedValidationContent
            validationState={validationState}
            origin={ValidationOrigin.BADGE}
            badgeLabelNr={badgeLabelNr}
        />
    );
}
